// src/components/video/artStyles.js
//
// The picture LOOK for a whole lesson video, picked with buttons in the
// Lesson video studio (no typing). Saved on the storyboard as:
//
//   artStyle    one of the keys below ("" = the SBL default cartoon look)
//   background  "scene" | "plain" | "color" | ""   (""= let the picture decide)
//   noPeople    true = no people, characters or animals with faces
//   sameDrawing true = every picture is the FIRST picture redrawn, changing
//               only what that line / colour group's directions say
//
// Every AI picture for the song gets the same words, so the whole video
// stays in one style. The thumbnails are the fish chart from Shug's style
// sheet, one goldfish per style.

import acrylic from "./artStyles/acrylic.jpg";
import anime from "./artStyles/anime.jpg";
import cartoon2d from "./artStyles/cartoon2d.jpg";
import chalk from "./artStyles/chalk.jpg";
import comic from "./artStyles/comic.jpg";
import crayon from "./artStyles/crayon.jpg";
import digital from "./artStyles/digital.jpg";
import doodle from "./artStyles/doodle.jpg";
import lineart from "./artStyles/lineart.jpg";
import minimalist from "./artStyles/minimalist.jpg";
import mosaic from "./artStyles/mosaic.jpg";
import pastel from "./artStyles/pastel.jpg";
import pencil from "./artStyles/pencil.jpg";
import pixel from "./artStyles/pixel.jpg";
import realistic from "./artStyles/realistic.jpg";
import render3d from "./artStyles/render3d.jpg";
import silhouette from "./artStyles/silhouette.jpg";
import stainedglass from "./artStyles/stainedglass.jpg";
import vector from "./artStyles/vector.jpg";
import watercolor from "./artStyles/watercolor.jpg";
import woodcut from "./artStyles/woodcut.jpg";

export const ART_STYLES = [
  {
    key: "cartoon2d",
    label: "2D Cartoon",
    img: cartoon2d,
    prompt:
      "2D cartoon illustration: bold clean black outlines, flat bright colours, simple rounded shapes, friendly and cheerful, front-facing flat perspective.",
  },
  {
    key: "digital",
    label: "Digital Art",
    img: digital,
    prompt:
      "Polished digital painting: smooth soft shading, rich glowing colours, gentle highlights, no hard outlines, storybook-quality finish.",
  },
  {
    key: "realistic",
    label: "Realistic",
    img: realistic,
    prompt: "Realistic, true-to-life rendering with natural colours, accurate detail and soft natural lighting.",
  },
  {
    key: "watercolor",
    label: "Watercolor",
    img: watercolor,
    prompt:
      "Watercolour painting: soft transparent washes, gentle colour bleeds, visible paper texture, a few light paint splatters.",
  },
  {
    key: "pencil",
    label: "Pencil Sketch",
    img: pencil,
    prompt: "Graphite pencil sketch: grey pencil lines and hatching on white paper, hand-drawn shading, no colour.",
  },
  {
    key: "lineart",
    label: "Line Art",
    img: lineart,
    prompt: "Clean black line art on white: even outlines only, no fill colour and no shading, like a colouring-book page.",
  },
  {
    key: "crayon",
    label: "Crayon",
    img: crayon,
    prompt: "Crayon drawing: waxy crayon strokes and texture, bright colours, slightly rough edges, like a child's best drawing.",
  },
  {
    key: "render3d",
    label: "3D Render",
    img: render3d,
    prompt: "Cute 3D render: soft rounded shapes, smooth clay-like materials, soft studio lighting, gentle shadows.",
  },
  {
    key: "pixel",
    label: "Pixel Art",
    img: pixel,
    prompt: "Retro pixel art: chunky square pixels, limited bright palette, crisp pixel edges, video-game look.",
  },
  {
    key: "vector",
    label: "Vector Art",
    img: vector,
    prompt: "Flat vector art: clean geometric shapes, solid flat colours, no outlines, no texture, modern and simple.",
  },
  {
    key: "chalk",
    label: "Chalk",
    img: chalk,
    prompt: "Chalk drawing: dusty chalk strokes and texture, like chalk on a dark chalkboard.",
  },
  {
    key: "comic",
    label: "Comic Book",
    img: comic,
    prompt: "Comic-book art: thick inked outlines, bold flat colours, light halftone shading, punchy and dynamic.",
  },
  {
    key: "stainedglass",
    label: "Stained Glass",
    img: stainedglass,
    prompt: "Stained-glass style: shapes split into bright jewel-coloured glass panels with thick dark lead lines.",
  },
  {
    key: "acrylic",
    label: "Acrylic Paint",
    img: acrylic,
    prompt: "Acrylic painting: visible thick brush strokes, rich layered colour, textured painterly finish.",
  },
  {
    key: "pastel",
    label: "Pastel",
    img: pastel,
    prompt: "Soft pastel drawing: chalky blended pastel strokes, light airy colours, textured paper.",
  },
  {
    key: "minimalist",
    label: "Minimalist",
    img: minimalist,
    prompt: "Minimalist illustration: very few simple flat shapes, two or three colours, lots of empty space, no detail or texture.",
  },
  {
    key: "doodle",
    label: "Doodle",
    img: doodle,
    prompt: "Hand-drawn doodle: playful thin black pen lines, a few little marks around it, mostly white, notebook-sketch feel.",
  },
  {
    key: "woodcut",
    label: "Woodcut",
    img: woodcut,
    prompt: "Woodcut print: bold carved black lines and fine engraved hatching, black ink on white, vintage print look.",
  },
  {
    key: "anime",
    label: "Anime/Manga",
    img: anime,
    prompt: "Anime / manga style: clean outlines, big expressive shiny eyes, smooth cel shading, bright colours.",
  },
  {
    key: "mosaic",
    label: "Mosaic",
    img: mosaic,
    prompt: "Mosaic: made of small square coloured tiles with thin gaps between them, bright varied colours.",
  },
  {
    key: "silhouette",
    label: "Silhouette",
    img: silhouette,
    prompt: "Silhouette: solid black shapes with almost no inner detail, crisp edges, high contrast.",
  },
];

export const artStyleByKey = (key) => ART_STYLES.find((s) => s.key === key) || null;

/** Background choices — buttons in the studio. */
export const BACKGROUNDS = [
  { key: "", label: "Let the picture decide", short: "auto", prompt: "" },
  {
    key: "plain",
    label: "No background — plain white",
    short: "plain white",
    prompt:
      "NO background: the subject sits on a completely plain, flat, empty white background. No scenery, no room, no floor, no sky, no props, no shadows on the ground.",
  },
  {
    key: "color",
    label: "Plain soft colour",
    short: "soft colour",
    prompt:
      "Plain background: one flat, soft, light pastel colour behind the subject. No scenery and no extra objects.",
  },
  {
    key: "scene",
    label: "With a background scene",
    short: "scene",
    prompt: "Include a simple, friendly background scene that fits the subject, kept soft so the subject stands out.",
  },
];

export const backgroundByKey = (key) => BACKGROUNDS.find((b) => b.key === (key || "")) || BACKGROUNDS[0];

/**
 * The look words sent with every picture of a song. Empty when nothing is
 * picked (the Image studio's SBL style then applies).
 */
export const lookDirections = ({ artStyle = "", background = "", noPeople = false } = {}) => {
  const st = artStyleByKey(artStyle);
  const bg = backgroundByKey(background);
  return [
    st ? `Art style for EVERY picture in this video (keep it identical): ${st.prompt}` : "",
    bg.prompt,
    noPeople ? "No people, no characters, no faces and no animals — only the subject described." : "",
  ]
    .filter(Boolean)
    .join(" ");
};

/** Colours for picture groups — same colour = same picture. */
export const GROUP_COLORS = [
  { key: "red", hex: "#ef4444", name: "Red" },
  { key: "blue", hex: "#3b82f6", name: "Blue" },
  { key: "green", hex: "#22c55e", name: "Green" },
  { key: "yellow", hex: "#eab308", name: "Yellow" },
  { key: "purple", hex: "#a855f7", name: "Purple" },
  { key: "orange", hex: "#f97316", name: "Orange" },
  { key: "pink", hex: "#ec4899", name: "Pink" },
  { key: "teal", hex: "#14b8a6", name: "Teal" },
  { key: "brown", hex: "#a16207", name: "Brown" },
  { key: "navy", hex: "#1e3a8a", name: "Navy" },
  { key: "lime", hex: "#84cc16", name: "Lime" },
  { key: "grey", hex: "#64748b", name: "Grey" },
];

export const groupColor = (key) => GROUP_COLORS.find((c) => c.key === key) || GROUP_COLORS[0];

/** Camera moves on a picture, done by the video (one picture, many shots). */
export const CAMERAS = [
  { key: "", label: "Slow push-in (default)" },
  { key: "still", label: "Hold still" },
  { key: "zoomBottom", label: "Zoom in to the bottom (labels under things)" },
  { key: "zoomLeft", label: "Zoom in to the left side (e.g. a graph's scale)" },
  { key: "zoomRight", label: "Zoom in to the right side" },
  { key: "zoomTop", label: "Zoom in to the top" },
  { key: "zoomCenter", label: "Zoom in to the middle" },
  { key: "panRight", label: "Slide across, left → right" },
];

export const cameraLabel = (key) => CAMERAS.find((c) => c.key === (key || ""))?.label || CAMERAS[0].label;
